import type { AmplitudeFeatureSet } from '@/stores/featureToggles/amplitudeFeatures.types';

/**
 * Pure rules for the Amplitude feature-toggle slice: the feature-id catalogue, the
 * variant-value projection, the all-OFF factory and the dual-gate AND rule.
 *
 * Deliberately free of Vue, Pinia and the analytics vendor SDK, so every rule is unit-testable
 * with no framework in scope. The async population lives in the Model store.
 */

/**
 * Amplitude feature-flag ids. Each value MUST match the flag key configured in the Amplitude
 * project — they are the wire identifiers the experiment client resolves variants for.
 */
export enum AmplitudeFeature {
    HipSpine = 'hip-spine',
}

/** The single variant value that enables a feature. */
export const AMPLITUDE_ENABLED_VARIANT_VALUE = 'on';

/**
 * Strict projection of a resolved variant value onto a boolean: only the exact value
 * `on` enables. Everything else is OFF — a different value, a different letter case,
 * surrounding whitespace, or `undefined` for a flag the project does not serve.
 */
export function isAmplitudeVariantEnabled(variantValue: string | undefined): boolean {
    return variantValue === AMPLITUDE_ENABLED_VARIANT_VALUE;
}

/**
 * A fresh, fully-OFF feature set. Backs the store's initial state and every fail-closed
 * path, so a failed resolution can never leave a feature enabled. Returns a new object on
 * each call — callers commit it as reactive state and must not share one instance.
 */
export function allAmplitudeFeaturesOff(): AmplitudeFeatureSet {
    return Object.fromEntries(
        Object.values(AmplitudeFeature).map((feature) => [feature, false]),
    ) as AmplitudeFeatureSet;
}

/**
 * A dual-gated feature is visible only when BOTH sources agree — an additive restriction,
 * never a relaxation. Both parameters are `boolean` so the compiler, rather than a
 * convention, rejects truthiness coercion at the call site.
 */
export function isDualGateEnabled(
    orgFeatureEnabled: boolean,
    amplitudeFeatureEnabled: boolean,
): boolean {
    return orgFeatureEnabled && amplitudeFeatureEnabled;
}
